import React from "react";
import { Loader } from "smart-react";
import { isFeatureSubscribed } from "../../../../Utils/Auth/ValidatePermissions";
import { ListViewItemRender } from "./../ListView/ListView";
import {
  isButtonEnabled,
  showSubscribedLabel,
  showExpiredLabel,
  showFeatureExpirationDate,
} from "../Utils/Util";
import ProductListHOC from "../ProductListHOC";
import { PRODUCT_TRIAL_MESSAGE } from "../../../../constants/notificationMessages";
import {
  ProductAccordionItem,
  ProductButton,
  ProductCheckbox,
  ProductDialog,
  ProductEmptyState,
  ProductIconBadge,
  ProductMetaDot,
  ProductSummaryStrip,
  StatusTag,
} from "../Components/ProductListPrimitives";
import {
  getPortfolioMetrics,
  getProductMetrics,
} from "../Utils/ProductMetrics";

const ProductList = ({
  tenantID,
  isOrganizationProduct,
  subscribed = true,
  products,
  loader,
  expanded,
  setExpanded,
  visibleDialog,
  toggleDialog,
  aggrement,
  handleAgreementChange,
  isError,
  handleSubscribeFeature,
  handleSubscribeAll,
  submitDialog,
  isFeaturesEnabled,
  isAllProductFeaturesSubscribed,
  isAllProductFeaturesExpired,
  isAnyProductFeatureExpired,
  isOrganizationAdminRole,
  isWorldTenant,
  hasFeaturePermissions,
  user,
}) => {
  const showButtonTitle = ({ feature }) =>
    !feature?.IsEnabled && !isFeatureSubscribed(feature);

  const subscribeButtonClick = (feature) => {
    handleSubscribeFeature(feature?.feature_id);
  };

  const toggleProduct = (productId) => {
    setExpanded(expanded === productId ? "" : productId);
  };

  const visibleProducts = React.useMemo(
    () =>
      (products ?? []).filter(
        (product) =>
          (subscribed && hasFeaturePermissions(product?.features)) ||
          !subscribed,
      ),
    [hasFeaturePermissions, products, subscribed],
  );

  const summary = React.useMemo(
    () => getPortfolioMetrics(visibleProducts),
    [visibleProducts],
  );


  return (
    <>
      {loader && <Loader />}

      <div className="flex flex-col">
        {visibleProducts.length > 0 ? (
          <>

            {visibleProducts.map((product) =>
              (() => {
                const metrics = getProductMetrics(product);
                const canSubscribeAll =
                  !isFeaturesEnabled(product) ||
                  (isOrganizationProduct && !isOrganizationAdminRole(user)) ||
                  isAllProductFeaturesSubscribed(product) ||
                  isWorldTenant(tenantID) ||
                  subscribed
                    ? false
                    : true;

                return (
                  <ProductAccordionItem
                    key={product.product_id}
                    open={expanded === product.product_id}
                    onToggle={() => toggleProduct(product.product_id)}
                    title={
                      <div className="flex min-w-0 items-center gap-3">
                        <ProductIconBadge productName={product.display_name} />
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2.5">
                            <span className="leading-relaxed truncate text-[16px] font-semibold tracking-[-0.02em] text-foreground sm:text-[17px]">
                              {product.display_name}
                            </span>
                            {isAllProductFeaturesExpired(product?.features) && (
                              <StatusTag tone="error">Expired</StatusTag>
                            )}
                            {isAllProductFeaturesSubscribed(product) &&
                              !isAnyProductFeatureExpired(
                                product?.features,
                              ) && (
                                <StatusTag tone="success">Subscribed</StatusTag>
                              )}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-[13px] font-medium text-muted-foreground">
                            <span>{metrics.moduleCount} Modules</span>
                            <ProductMetaDot />
                            <span>{metrics.activeCount} Active</span>
                            {metrics.expiringSoonCount > 0 ? (
                              <>
                                <ProductMetaDot />
                                <span>
                                  {metrics.expiringSoonCount} Expiring Soon
                                </span>
                              </>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    }
                    action={
                      canSubscribeAll ? (
                        <ProductButton
                          size="lg"
                          onClick={() => handleSubscribeAll(product.product_id)}
                        >
                          Subscribe
                        </ProductButton>
                      ) : null
                    }
                  >
                    <div className="flex flex-col">
                      {product?.features?.map((feature) => (
                        <ListViewItemRender
                          key={feature.feature_id}
                          dataItem={feature}
                          isOrganizationProduct={isOrganizationProduct}
                          isOrganizationAdmin={isOrganizationAdminRole(user)}
                          subscribed={subscribed}
                          tenantID={tenantID}
                          isButtonEnabled={isButtonEnabled}
                          showSubscribedLabel={showSubscribedLabel}
                          showExpiredLabel={showExpiredLabel}
                          showFeatureExpirationDate={showFeatureExpirationDate}
                          showButtonTitle={showButtonTitle}
                          subscribeButtonClick={subscribeButtonClick}
                          isEditFeatureButton={() => false}
                        />
                      ))}
                    </div>
                  </ProductAccordionItem>
                );
              })(),
            )}
          </>
        ) : !loader ? (
          <ProductEmptyState
            title={
              subscribed ? "No subscribed products yet" : "No products found"
            }
            description={
              subscribed
                ? "Subscribed products and modules will appear here as soon as access is available."
                : "We couldn't find any products to show right now."
            }
          />
        ) : null}
      </div>

      <ProductDialog
        open={visibleDialog}
        onClose={toggleDialog}
        title="Please Confirm"
        description={PRODUCT_TRIAL_MESSAGE?.description}
        footer={
          <>
            <ProductButton variant="outline" onClick={toggleDialog}>
              Cancel
            </ProductButton>
            <ProductButton onClick={submitDialog}>Submit</ProductButton>
          </>
        }
      >
        <ProductCheckbox
          checked={aggrement}
          onCheckedChange={handleAgreementChange}
          label="I acknowledge that I meet the requirements of the trial"
          error={isError}
        />
      </ProductDialog>
    </>
  );
};

export default ProductListHOC(ProductList);
